import { Box, Container, Fade, IconButton, Paper, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridToolbar,
} from "@mui/x-data-grid";
import { useContext, useState } from "react";
import {
  AlertComponent,
  ModalComponent,
  NavBar,
  ProductForm,
  ShowContent,
  Spinner,
} from "../../components";
import DataContext from "../../context/DataContext";
import PaginatorContext from "../../context/PaginatorContext";
import { useFetch, usePermissions } from "../../hooks";
import { ProductProps } from "../../interfaces/interfaces";
import EditIcon from "@mui/icons-material/Edit";
import { initial_tool_state } from "../../utils/data";
import AddBoxIcon from "@mui/icons-material/AddBox";
import {
  modernDataGridStyles,
  modernIconButtonStyles,
} from "../../utils/dataGridStyles";

interface FetchResponse {
  success?: boolean;
  data: ProductProps[];
}

const initialProductState: ProductProps = {
  id: 0,
  partNumber: "",
  name: "",
  description: "",
  supplier: "",
  topThreadConnection: "",
  bottomThreadConnection: "",
  status: { id: 0, name: "" },
  maxOD: "",
  bodyOD: "",
  length: 0,
  quantity: 1,
  weight: 0,
  osi: false,
  tailJoint: false,
  espAssembly: false,
  imagePath: null,
  size: 0,
  isCompound: false,
};

export default function ProductPage() {
  const theme = useTheme();
  const { isSuccess, isError, onCreateTool, onEditTool } = useContext(DataContext);
  const { paginationModel, fetchPaginationModel } =
    useContext(PaginatorContext);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [productInfo, setProductInfo] =
    useState<ProductProps>(initialProductState);
  const matches = useMediaQuery("(min-width:600px)");
  const { canManageProducts } = usePermissions();

  const {
    data,
    pagination,
    isLoading,
    fetchData,
  } = useFetch<FetchResponse>("product", paginationModel);

  const handleEditProduct = (product: ProductProps) => {
    setProductInfo(product);
    setIsOpenModal(true);
  };

  const columns: GridColDef[] = [
    {
      field: "action",
      headerName: "Actions",
      renderHeader: () => <strong>{"ACTIONS"}</strong>,
      width: 80,
      filterable: false,
      sortable: false,
      renderCell: (params: any) => {
        return (
          <div className="action-buttons">
            {canManageProducts && (
              <Tooltip title="Edit">
                <IconButton
                  onClick={() => handleEditProduct(params.row)}
                  sx={modernIconButtonStyles(theme)}
                  size="small"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      field: "partNumber",
      renderHeader: () => <strong>{"PART NUMBER"}</strong>,
      width: 180,
    },
    {
      field: "name",
      renderHeader: () => <strong>{"NAME"}</strong>,
      width: 300,
    },
    {
      field: "topThreadConnection",
      renderHeader: () => <strong>{"TOP THREAD"}</strong>,
      width: 150,
      valueGetter: (params: any) => params.row?.topThreadConnection ?? "",
    },
    {
      field: "bottomThreadConnection",
      renderHeader: () => <strong>{"BOTTOM THREAD"}</strong>,
      width: 150,
      valueGetter: (params: any) => params.row?.bottomThreadConnection ?? "",
    },
    {
      field: "maxOD",
      renderHeader: () => <strong>{"MAX OD"}</strong>,
      width: 100,
      valueGetter: (params: any) => params.row?.maxOD ?? "",
    },
    {
      field: "bodyOD",
      renderHeader: () => <strong>{"BODY OD"}</strong>,
      width: 100,
      valueGetter: (params: any) => params.row?.bodyOD ?? "",
    },
    {
      field: "length",
      renderHeader: () => <strong>{"LENGTH"}</strong>,
      width: 100,
      valueGetter: (params: any) => params.row?.length ?? "",
    },
    {
      field: "weight",
      renderHeader: () => <strong>{"WEIGHT"}</strong>,
      width: 100,
      valueGetter: (params: any) => params.row?.weight ?? "",
    },
    {
      field: "description",
      renderHeader: () => <strong>{"DESCRIPTION"}</strong>,
      width: 300,
      valueGetter: (params: any) => params.row?.description ?? "",
    },
  ];

  const buttons = canManageProducts
    ? [
        {
          title: "New",
          action: () => {
            setProductInfo(initial_tool_state);
            setIsOpenModal(true);
          },
          icon: <AddBoxIcon />,
        },
      ]
    : [];

  let content: JSX.Element | JSX.Element[];

  if (isLoading) {
    content = <Spinner />;
  } else {
    content = (
      <>
        <NavBar title="Products" buttons={buttons} />
        <Box
          sx={{
            marginTop: "80px",
            minHeight: "calc(100vh - 80px)",
            background:
              "linear-gradient(135deg, rgba(241, 236, 231, 0.8) 0%, rgba(241, 236, 231, 0.4) 100%)",
            padding: { xs: 2, md: 4 },
          }}
        >
          <Container maxWidth="xl">
            <Fade in={true} timeout={600}>
              <Paper
                elevation={3}
                sx={{
                  padding: { xs: 2, md: 4 },
                  borderRadius: "20px",
                  background: "rgba(255, 255, 255, 0.95)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(140, 150, 169, 0.1)",
                  boxShadow: "0 8px 32px rgba(140, 150, 169, 0.15)",
                }}
              >
                <Box sx={{ height: "70vh", width: "100%" }}>
                  <DataGrid
                    density={matches ? "standard" : "compact"}
                    sx={modernDataGridStyles(theme)}
                    rows={data?.data ?? []}
                    columns={columns}
                    slots={{ toolbar: GridToolbar }}
                    loading={isLoading}
                    rowCount={+pagination?.totalRecords}
                    paginationMode="server"
                    paginationModel={paginationModel}
                    onPaginationModelChange={fetchPaginationModel}
                  />
                </Box>
              </Paper>
            </Fade>
          </Container>
        </Box>
        {isSuccess && <AlertComponent type="success" />}
        {isError && <AlertComponent type="error" />}
        {isOpenModal && (
          <ModalComponent
            modalContent={
              productInfo.id === 0 ? (
                <ProductForm
                  productValues={productInfo}
                  onCreatetool={(values: ProductProps) => {
                    onCreateTool(values, () => {
                      fetchData();
                      setIsOpenModal(false);
                    });
                  }}
                  onCancel={() => {
                    setProductInfo(initialProductState);
                    setIsOpenModal(false);
                  }}
                />
              ) : (
                <ProductForm
                  productValues={productInfo}
                  onEdit={(values: ProductProps) => {
                    onEditTool(values, undefined, () => {
                      fetchData();
                      setIsOpenModal(false);
                    });
                  }}
                  onCancel={() => {
                    setProductInfo(initialProductState);
                    setIsOpenModal(false);
                  }}
                />
              )
            }
          />
        )}
      </>
    );
  }

  return <ShowContent error={""} isLoading={false} content={content} />;
}
